import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Divider, Drawer, IconButton, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  FEATURES, QUADRANTS, STAGES, getHealth, getMisuse, getPotential, getQuadrant, getStage, nextAction,
} from '../../utils/platformKpi';
import { HEALTH_COLORS, POTENTIAL_LABELS, SampleChip, formatAed, formatBytes, formatCount, planLabel } from './common';

const Block = ({ title, sample, children }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'secondary.main', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </Typography>
      {sample && <SampleChip />}
    </Box>
    {children}
  </Box>
);

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{value ?? '—'}</Typography>
  </Box>
);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/** One center's snapshot: ladder, potential, adoption, misuse, subscription (design §4.7). */
const CenterDrawer = ({ center, sources = {}, open, onClose }) => {
  const navigate = useNavigate();
  if (!center) return null;
  const stageIndex = STAGES.findIndex((s) => s.key === getStage(center));
  const health = getHealth(center);
  const misuse = getMisuse(center);
  const potential = getPotential(center);
  const quadrant = potential ? QUADRANTS[getQuadrant(center)] : null;
  const usageSample = sources.usage === 'sample';

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700 }} noWrap>{center.name}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {center.code} · joined {fmtDate(center.createdAt)}{center.active ? '' : ' · disabled'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {usageSample && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <SampleChip />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            Health, stage, potential and activity below are sample data until the KPI API is ready.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        <Chip size="small" label={{ red: 'At risk', amber: 'Quiet', green: 'Healthy' }[health.level]}
          sx={{ bgcolor: HEALTH_COLORS[health.level], color: '#fff', fontWeight: 600 }} />
        {quadrant && <Chip size="small" label={quadrant.label} color="primary" />}
      </Box>

      <Box sx={{ p: 1.25, mb: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Next action</Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'primary.main' }}>{nextAction(center)}</Typography>
        {health.reasons.map((r) => (
          <Typography key={r} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>• {r}</Typography>
        ))}
      </Box>

      <Block title="Usage ladder" sample={usageSample}>
        <Stepper activeStep={stageIndex + 1} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.65rem', mt: 0.5 } }}>
          {STAGES.map((s) => <Step key={s.key}><StepLabel>{s.label}</StepLabel></Step>)}
        </Stepper>
      </Block>

      <Block title="Potential" sample={usageSample}>
        <Row label="Potential" value={potential ? POTENTIAL_LABELS[potential] : null} />
        <Row label="Clients" value={formatCount(center.clients)} />
        <Row label="Client employees" value={formatCount(center.employees)} />
        <Row label="Added this period" value={`${formatCount(center.clientsAdded)} clients · ${formatCount(center.employeesAdded)} employees`} />
      </Block>

      <Block title="Features used (30 days)" sample={usageSample}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {FEATURES.map((f) => {
            const used = center.features?.[f.key];
            return (
              <Chip key={f.key} size="small" label={f.label}
                sx={{ fontSize: '0.7rem', bgcolor: used ? 'success.main' : 'action.disabledBackground', color: used ? '#fff' : 'text.secondary' }} />
            );
          })}
        </Box>
      </Block>

      {misuse.length > 0 && (
        <Block title="Not using it properly" sample={usageSample}>
          {misuse.map((m) => (
            <Box key={m.key} sx={{ mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{m.label}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'primary.main' }}>{m.action}</Typography>
            </Box>
          ))}
        </Block>
      )}

      <Block title="Subscription" sample={sources.plan === 'sample'}>
        <Row label="Plan" value={center.plan ? planLabel(center.plan) : null} />
        <Row label="Status" value={center.plan?.status} />
        <Row label="Period ends" value={center.plan?.periodEnd} />
        <Row label="Auto-renew" value={center.plan?.autoRenew == null ? null : (center.plan.autoRenew ? 'On' : 'Off')} />
      </Block>

      <Block title="Activity & cost" sample={usageSample}>
        <Row label="Last business activity" value={fmtDate(center.lastBusinessAt)} />
        <Row label="Last login activity" value={fmtDate(center.lastActiveAt)} />
        <Row label="Work orders (30 days)" value={formatCount(center.wo30d)} />
        <Row label="Invoiced this period" value={formatAed(center.gmvInvoiced)} />
        <Row label="AI tokens this period" value={formatCount(center.aiTokens)} />
        <Row label="Storage" value={formatBytes(center.storageBytes)} />
      </Block>

      <Divider sx={{ my: 1 }} />
      <Button variant="contained" fullWidth size="small" onClick={() => navigate(`/typing-centers/${center.id}`)}>
        Open center
      </Button>
    </Drawer>
  );
};

export default CenterDrawer;
